import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { GlobalSearch } from "@/components/search/GlobalSearch";
import { PortalProvider, useIsAdminPortal } from "@/lib/portal";
import { useSidebarCollapsed } from "@/lib/prefs";
import { ConnectivityBanner } from "@/components/pwa/ConnectivityBanner";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { PullToRefresh } from "@/components/pwa/PullToRefresh";
import { RouteTransition } from "@/components/pwa/RouteTransition";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { QuickActions } from "./QuickActions";
import { Sidebar } from "./Sidebar";
import { SubscriptionGuard } from "./SubscriptionGuard";
import { Topbar } from "./Topbar";

function Shell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();
  // Single source of truth for the portal — no prop drilling, no ad-hoc
  // pathname checks. Every layout child reads the same context. [audit P0-3b]
  const adminPortal = useIsAdminPortal();
  // Desktop rail state drives the content offset; mirrors the same pref the
  // Sidebar reads, so margin and rail width stay in lock-step.
  const [collapsed] = useSidebarCollapsed();

  return (
    <div className={cn("min-h-full min-w-0", !adminPortal && "mesh-bg")}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:end-4 focus:top-4 focus:z-[1000] focus:rounded-md focus:bg-navy focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lift"
      >
        {t("common.skipToContent")}
      </a>
      {/* PWA connectivity status — slim banner above the chrome. [task #15] */}
      <ConnectivityBanner />
      <ImpersonationBanner />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div
        className={cn(
          "flex min-h-full min-w-0 flex-col transition-[margin] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
          // Logical inline-start margin so the offset flips correctly under RTL;
          // collapse only affects desktop (lg), matching the rail.
          collapsed ? "lg:ms-16" : "lg:ms-sidebar",
        )}
      >
        <Topbar onMenuClick={() => setSidebarOpen(true)} />
        {/* Pull-to-refresh wraps the scrolling content; inert on desktop and
            under reduced motion. Default refresh = full reload. [task #15] */}
        <PullToRefresh className="flex flex-1 flex-col">
          <main
            id="main"
            tabIndex={-1}
            className="min-w-0 flex-1 px-3.5 pb-7 pt-0 focus:outline-none sm:px-5 md:px-7 md:pb-8"
          >
            <ErrorBoundary compact>
              <RouteTransition>
                <SubscriptionGuard>
                  <Outlet />
                </SubscriptionGuard>
              </RouteTransition>
            </ErrorBoundary>
          </main>
        </PullToRefresh>
      </div>
      <GlobalSearch />
      <QuickActions />
      {/* Add-to-Home-Screen affordance — hidden once installed/dismissed. */}
      <InstallPrompt />
    </div>
  );
}

export function AppShell() {
  return (
    <PortalProvider>
      <Shell />
    </PortalProvider>
  );
}
